import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  HostBinding,
  inject,
  input,
  OnInit
} from '@angular/core';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {Checkbox} from "primeng/checkbox";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {RouterLink} from "@angular/router";
import {MessageService} from "primeng/api";
import {Subject} from "rxjs";

/** Per control, so the checkbox bound to it shakes even when a social button elsewhere asks. */
const shakeRequests = new WeakMap<FormControl<boolean>, Subject<void>>();

function shakeRequestsFor(control: FormControl<boolean>): Subject<void> {
  let requests = shakeRequests.get(control);
  if (!requests) {
    requests = new Subject<void>();
    shakeRequests.set(control, requests);
  }
  return requests;
}

/**
 * For a social sign-in button tapped before this checkbox is ticked: shakes the checkbox, shows its
 * invalid state (from touched) and toasts why. The toast is a fresh object per call, because
 * PrimeNG's Toast tracks messages by reference and a shared one confuses repeated taps.
 */
export function flagTermsRequired(termsControl: FormControl<boolean>, messageService: MessageService) {
  termsControl.markAsTouched();
  shakeRequestsFor(termsControl).next();
  messageService.add({
    severity: 'warn',
    summary: 'Terms not accepted',
    detail: 'You must agree to the terms and conditions first.',
  });
}

const shakeKeyframes: Keyframe[] = [
  {transform: 'translateX(0)'},
  {transform: 'translateX(-6px)'},
  {transform: 'translateX(6px)'},
  {transform: 'translateX(-6px)'},
  {transform: 'translateX(6px)'},
  {transform: 'translateX(0)'},
];

@Component({
  selector: 'homey-terms-conditions-checkbox',
  imports: [
    Checkbox,
    ReactiveFormsModule,
    RouterLink
  ],
  templateUrl: './terms-conditions-checkbox.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TermsConditionsCheckboxComponent implements OnInit {
  @HostBinding('class') class = 'flex gap-2 items-center mb-6';

  formControlInput = input.required<FormControl<boolean>>();

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit() {
    const control = this.formControlInput();
    // OnPush: re-render when the control is touched from outside (flagTermsRequired, a form submit),
    // so the invalid state shows.
    control.events.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => this.changeDetectorRef.markForCheck());
    shakeRequestsFor(control).pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
        // A new animate() call replays the shake, even over one still running.
        this.elementRef.nativeElement.animate(shakeKeyframes, {duration: 400, easing: 'ease-in-out'});
      }
    });
  }
}
